import React, { useRef, useState } from 'react'
import { Box, Stack } from '@mui/material'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core'
import { bulkImportSubjectsSchema } from 'schemas/subjects'
import { SubjectStatus, SubjectType, SubjectTypesList } from 'constants/index'
import { nanoid } from 'nanoid'
import {
  bulkInsertExistingSubjects,
  bulkInsertSubjects,
  notifySubjectsToUpdateInformation,
  resetSubject,
  scanImportedSubjects
} from 'store/subject'
import { Form, Formik } from 'formik'
import CustomSelect from 'components/Form/CustomSelect'
import ProgressButton from 'components/ProgressButton'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { InfoRounded } from '@material-ui/icons'
import { parse } from 'papaparse'
import { isValidEmail, parseCsvData, removeDuplicateEmails, SubjectsNameConcatenation } from 'utils'
import { colors } from 'utils/colors'
import { toast } from 'react-toastify'

const BulkImportSubjectsForm = ({ conferenceId }) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const history = useHistory()

  const inputFileRef = useRef()
  const [data, setData] = useState([])
  const [csvFile, setCsvFile] = useState(null)
  const [csvError, setCsvError] = useState('')
  const [csvInfoMsg, setCsvInfoMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [dataModal, setDataModal] = useState(false)
  const [scannedData, setScannedData] = useState(null)

  const { conference } = useSelector((state) => state.conference)

  const initialValues = {
    type: ''
  }

  const goBack = () => {
    dispatch(resetSubject())
    history.goBack()
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      if (file.type !== 'text/csv') {
        setCsvError('Invalid file type. Please upload a CSV file.')
        setCsvFile(file)
        return
      }
      setCsvFile(file)
      parse(file, {
        header: false,
        complete: async (results) => {
          const data = results.data
          data.pop()
          const parsedData = parseCsvData(data)
          const uniqueData = removeDuplicateEmails(parsedData, 'email')
          const validData = uniqueData?.filter((doc) => doc?.email && isValidEmail(doc?.email))
          const invalidData = uniqueData?.length - validData?.length
          if (!uniqueData?.length) {
            setCsvError('The CSV file must contains at least one valid email address.')
          } else if (invalidData && uniqueData?.length !== invalidData) {
            setCsvInfoMsg(
              `${invalidData} Subject(s) will be rejected due to invalid email formats.`
            )
            setData(validData)
          } else if (uniqueData?.length === invalidData) {
            setCsvError(`All Subject(s) have been rejected due to invalid email formats.`)
          } else {
            setData(validData)
            setCsvError('')
            setCsvInfoMsg('')
          }
        },
        error: (error) => {
          setCsvError(error)
        }
      })
    } else {
      setCsvFile(null)
    }
    setCsvError('')
    setCsvInfoMsg('')
    event.target.value = null
  }

  const onSubmit = async (values) => {
    if (!csvError) {
      setLoading(true)
      const subjects = data.map((subject) => {
        const updatedSubject = {
          email: subject?.email,
          type: values.type,
          token: nanoid(),
          status: SubjectStatus.INVITE_NOT_SENT,
          conferenceId
        }
        if (values.type === SubjectType.ATTENDEE) {
          delete updatedSubject['token']
          updatedSubject.firstName = subject?.firstName ?? ''
          updatedSubject.lastName = subject?.lastName ?? ''
          updatedSubject.fullName = SubjectsNameConcatenation(
            subject?.firstName ?? '',
            subject?.lastName ?? ''
          )
        } else if (values.type === SubjectType.EXHIBITOR) {
          updatedSubject.name = SubjectsNameConcatenation(
            subject?.lastName ?? '',
            subject?.firstName ?? ''
          )
        } else if (values.type === SubjectType.SPEAKER) {
          updatedSubject.fullName = SubjectsNameConcatenation(
            subject?.lastName ?? '',
            subject?.firstName ?? ''
          )
        } else {
          updatedSubject.fullName = subject?.company ?? ''
        }
        return updatedSubject
      })
      if (values.type === SubjectType.ATTENDEE) {
        const results = await dispatch(bulkInsertSubjects(values.type, subjects, conferenceId))

        if (results.uniqueCount && !results.existingCount) {
          toast.success(`${results.uniqueCount} Subject(s) imported successfully.`)
        } else if (!results.uniqueCount && results.existingCount) {
          toast.error(
            `${results.existingCount} subject(s) have not been imported because subject(s) with the same email(s) already exist.`
          )
        } else {
          toast.success(`${results.uniqueCount} Subject(s) imported successfully.`)
          toast.error(
            `${results.existingCount} subject(s) have not been imported, because subject(s) with the same email(s) already exist.`
          )
        }
        setLoading(false)
        goBack()
      } else {
        setLoading(true)
        const result = await scanImportedSubjects(values.type, subjects, conferenceId)

        let uniqueCount = 0
        let existingCount = 0
        if (result?.brandNewSubjects?.length) {
          const results = await dispatch(
            bulkInsertSubjects(values.type, result?.brandNewSubjects, conferenceId)
          )
          uniqueCount += results.uniqueCount
          existingCount += results.existingCount
        }
        if (result?.existingSubjects?.length) {
          const results = await bulkInsertExistingSubjects(
            values.type,
            result?.existingSubjects,
            conferenceId
          )
          uniqueCount += results.uniqueCount
          existingCount += results.existingCount
          if (uniqueCount > 0) {
            await notifySubjectsToUpdateInformation(values.type, conferenceId)
          }
        }

        if (uniqueCount && !existingCount) {
          toast.success(`${uniqueCount} Subject(s) imported successfully.`)
        } else if (!uniqueCount && existingCount) {
          toast.error(
            `${existingCount} subject(s) have not been imported because subject(s) with the same email(s) already exist.`
          )
        } else {
          toast.success(`${uniqueCount} Subject(s) imported successfully.`)
          toast.error(
            `${existingCount} subject(s) have not been imported, because subject(s) with the same email(s) already exist.`
          )
        }

        if (!uniqueCount) {
          goBack()
          return
        }
        setScannedData({
          uniqueCount: uniqueCount,
          existingCount: result.existingSubjects?.length
        })
        setLoading(false)
        setDataModal(true)
      }
    }
  }

  return (
    <>
      <Box className={classes.headBar}>
        <Typography variant="h4">Import Subjects</Typography>
        <Button color="primary" variant="contained" onClick={goBack}>
          Back
        </Button>
      </Box>

      <Box p={1} border={1} sx={{ mb: '2rem' }}>
        <Box
          p={1}
          sx={{
            backgroundColor: '#ECECEC',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
          <Box className={classes.singleInfo}>
            <Typography className={classes.label}>Conference Name:</Typography>
            <Typography>{conference?.confName}</Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.note}>
        <Typography className={classes.noteHead}>
          Streamline data management for Attendees, Exhibitors, Speakers and Sponsors in your
          conference effortlessly.
        </Typography>
        <Box className={classes.noteSteps}>
          <Typography className={classes.noteValue}>
            1- Select the desired subject type (Attendee, Exhibitor, Speaker or Sponsor).
          </Typography>
          <Typography className={classes.noteValue}>
            2- Choose a CSV file and make sure it contains a column titled{' '}
            <span className={classes.bold}>&apos;Email&apos;</span> for subject&apos;s data.
          </Typography>
          <Typography className={classes.noteValue}>
            3- Once successfully imported, you can send invitations individually or in bulk for them
            to fill out their data.
          </Typography>
        </Box>
      </Box>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={bulkImportSubjectsSchema}
        onSubmit={onSubmit}>
        {({ values }) => (
          <Form autoComplete="off" style={{ marginBottom: '1rem', marginTop: '1rem' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography className={classes.emailLabel}>Subject Type</Typography>
                <CustomSelect
                  showLabel={false}
                  inputLabel="Subject Type"
                  options={SubjectTypesList}
                  optionAttr="type"
                  name="type"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography className={classes.emailLabel}>CSV Subject File Uploader</Typography>
                <Box>
                  <FormControl error={Boolean(csvError)}>
                    <Button
                      color="primary"
                      size="small"
                      className={classes.upload}
                      onClick={() => inputFileRef.current.click()}>
                      {csvFile ? csvFile?.name : 'Select a CSV file'}
                      <input
                        id="csv-file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        ref={inputFileRef}
                        className={classes.input}
                      />
                    </Button>
                  </FormControl>
                  {csvError && (
                    <FormHelperText className={classes.error}>{csvError}</FormHelperText>
                  )}
                  {csvInfoMsg && (
                    <Box className={classes.infoBox}>
                      <InfoRounded className={classes.infoIcon} />
                      <FormHelperText className={classes.info}>{csvInfoMsg}</FormHelperText>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={12}>
                <Box className={classes.buttonBox}>
                  <Button size="large" disabled={loading} onClick={goBack}>
                    Cancel
                  </Button>
                  <ProgressButton
                    type="submit"
                    inProgress={loading}
                    label="Import"
                    disabled={loading}
                    onClick={() => {
                      !csvFile && setCsvError('Please select CSV file to import subjects.')
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Dialog fullWidth maxWidth="md" open={dataModal}>
              <DialogTitle>
                <Typography variant="h6" className={classes.modalTitle}>
                  Import Summary
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Box>
                  <Stack spacing={1} className={classes.stack}>
                    <Box className={classes.row}>
                      <Typography className={classes.rowKey}>
                        Total {values.type}(s) Imported:
                      </Typography>
                      <Typography className={classes.rowValue}>
                        {scannedData?.uniqueCount}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography className={classes.noticeText}>
                    {scannedData?.existingCount ? (
                      <>
                        <strong>{scannedData?.existingCount}</strong>
                        {` ${values.type} profile(s) were found from previous conference and replicated. These profiles are moved to `}
                        <strong>Pending Review</strong>
                        {` status. An email is also sent to these ${values.type}(s) with a link to edit their profile if needed.`}
                      </>
                    ) : (
                      'No Profile found from previous conference.'
                    )}
                  </Typography>

                  <Box className={classes.actions}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        setDataModal(false)
                        setScannedData(null)
                        goBack()
                      }}>
                      Close
                    </Button>
                  </Box>
                </Box>
              </DialogContent>
            </Dialog>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default BulkImportSubjectsForm

const useStyles = makeStyles({
  stack: {
    width: '40%',
    marginBottom: '1.5rem'
  },
  modalTitle: {
    color: colors.indigo,
    letterSpacing: '0.5px',
    fontWeight: 600
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem'
  },
  rowKey: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#606060',
    lineHeight: '0px'
  },
  rowValue: {
    lineHeight: '0px',
    fontSize: '18px',
    fontWeight: 600
  },
  actions: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '0.7rem'
  },
  noticeText: {
    fontSize: '16px',
    letterSpacing: '0.4px'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem',
    alignItems: 'center',
    gap: '1rem'
  },
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0rem 0rem 2rem 0rem'
  },
  note: {
    padding: '12px',
    borderRadius: '12px',
    backgroundColor: '#caedf5'
  },
  noteSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingLeft: '8px'
  },
  noteValue: {
    marginTop: '0.2rem',
    fontSize: '14px',
    fontWeight: '500'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  emailLabel: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '8px',
    marginLeft: '5px'
  },
  label: {
    minWidth: '132px',
    fontSize: '15px',
    fontWeight: '600'
  },
  value: {
    fontSize: '14px',
    marginRight: '1rem'
  },
  buttonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '0.75rem'
  },
  upload: {
    textTransform: 'none',
    minWidth: '350px',
    justifyContent: 'start',
    padding: '15px',
    fontSize: '13px',
    fontWeight: 600,
    border: '1px solid #bdbdbd',
    borderRadius: '10px',
    background: '#FFFFFF',
    marginRight: '20px'
  },
  input: {
    display: 'none'
  },
  error: {
    marginTop: '5px',
    marginLeft: '12px',
    color: '#f44336'
  },
  infoIcon: {
    marginTop: '4px',
    fontSize: '16px',
    color: '#1e88e5'
  },
  infoBox: {
    marginTop: '4px',
    marginLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  info: {
    marginTop: '4px',
    color: '#1e88e5'
  },
  bold: {
    fontWeight: '600'
  }
})
